import api from './api';

export type SubAccountStatus = 'PENDING_ACTIVATION' | 'ACTIVE' | 'SUSPENDED';

export interface AppotaPaySubAccountRow {
  id: number;
  merchantId: number;
  merchantOwnerUserId?: number | null;
  merchantName?: string | null;
  merchantPhone?: string | null;
  merchantEmail?: string | null;
  referId?: string | null;
  hasCredentials?: boolean;
  environment?: string | null;
  status: SubAccountStatus;
  accountName?: string | null;
  note?: string | null;
  activatedAt?: string | null;
  lastErrorCode?: string | null;
  lastErrorMessage?: string | null;
  lastAttemptedAt?: string | null;
  provisioningAttempts?: number;
  appotapayBalanceVnd?: number | null;
  appotapayLastSyncedAt?: string | null;
  appotapaySyncError?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface AppotaPayPaymentRow {
  id: number;
  referenceId: string;
  transactionId?: string | null;
  status?: string | null;
  amountVnd?: number | null;
  paidAmountVnd?: number | null;
  channel?: string | null;
  paidAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface AppotaPaySubAccountDetail {
  ok?: boolean;
  source?: string;
  account: AppotaPaySubAccountRow;
  withdrawableVnd: number;
  withdrawableSource: string;
  recentPayments: AppotaPayPaymentRow[];
  synced?: boolean;
  balanceSuccess?: boolean;
  balanceError?: string | null;
}

export interface PartnerBalanceSummary {
  partnerBalanceVnd: number | null;
  merchantLiabilityVnd: number;
  shortfallVnd: number | null;
  source: string;
  balanceSuccess?: boolean;
  balanceError?: string | null;
}

export interface LookupResult {
  ok?: boolean;
  source?: string;
  lookupType?: string;
  referenceId?: string;
  partnerRefId?: string;
  transactionId?: string | null;
  type?: string;
  status?: string;
  data?: unknown;
  syncedAt?: string;
}

function unwrap<T>(res: {
  data?: { success?: boolean; data?: T; message?: string };
}): T {
  const body = res.data;
  if (
    body &&
    typeof body === 'object' &&
    'data' in body &&
    body.data !== undefined
  ) {
    return body.data as T;
  }
  return body as T;
}

type RawRecord = Record<string, unknown>;

function num(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function str(v: unknown): string | null {
  if (v == null) return null;
  const s = String(v).trim();
  return s || null;
}

function pick<T>(raw: RawRecord, snake: string, camel: string): T | undefined {
  if (raw[snake] !== undefined && raw[snake] !== null) return raw[snake] as T;
  if (raw[camel] !== undefined && raw[camel] !== null) return raw[camel] as T;
  return undefined;
}

/** Chuẩn hoá snake_case (DB) hoặc camelCase (DTO) → camelCase cho UI. */
export function mapAppotaPaySubAccountRow(
  raw: RawRecord
): AppotaPaySubAccountRow {
  const merchantId = num(pick(raw, 'merchant_id', 'merchantId')) ?? 0;
  const status = (pick<string>(raw, 'status', 'status') ||
    'PENDING_ACTIVATION') as SubAccountStatus;
  return {
    id: num(raw.id) ?? 0,
    merchantId,
    merchantOwnerUserId: num(
      pick(raw, 'merchant_owner_user_id', 'merchantOwnerUserId')
    ),
    merchantName: str(pick(raw, 'merchant_name', 'merchantName')),
    merchantPhone: str(pick(raw, 'merchant_phone', 'merchantPhone')),
    merchantEmail: str(pick(raw, 'merchant_email', 'merchantEmail')),
    referId: str(pick(raw, 'refer_id', 'referId')),
    hasCredentials: !!pick(raw, 'has_credentials', 'hasCredentials'),
    environment: str(pick(raw, 'appotapay_environment', 'environment')),
    status,
    accountName: str(pick(raw, 'account_name', 'accountName')),
    note: str(raw.note),
    activatedAt: str(pick(raw, 'activated_at', 'activatedAt')),
    lastErrorCode: str(pick(raw, 'last_error_code', 'lastErrorCode')),
    lastErrorMessage: str(pick(raw, 'last_error_message', 'lastErrorMessage')),
    lastAttemptedAt: str(pick(raw, 'last_attempted_at', 'lastAttemptedAt')),
    provisioningAttempts:
      num(pick(raw, 'provisioning_attempts', 'provisioningAttempts')) ?? 0,
    appotapayBalanceVnd: num(
      pick(raw, 'appotapay_balance_vnd', 'appotapayBalanceVnd')
    ),
    appotapayLastSyncedAt: str(
      pick(raw, 'appotapay_last_synced_at', 'appotapayLastSyncedAt')
    ),
    appotapaySyncError: str(
      pick(raw, 'appotapay_sync_error', 'appotapaySyncError')
    ),
    createdAt: str(pick(raw, 'created_at', 'createdAt')),
    updatedAt: str(pick(raw, 'updated_at', 'updatedAt')),
  };
}

function mapPartnerBalance(raw: RawRecord): PartnerBalanceSummary {
  const balanceSuccess =
    raw.balanceSuccess !== false && raw.balance_success !== false;
  const partnerBalanceRaw = pick(
    raw,
    'partner_balance_vnd',
    'partnerBalanceVnd'
  );
  const partnerBalanceVnd =
    partnerBalanceRaw == null || partnerBalanceRaw === ''
      ? null
      : num(partnerBalanceRaw);
  const merchantLiabilityVnd =
    num(pick(raw, 'merchant_liability_vnd', 'merchantLiabilityVnd')) ?? 0;
  const shortfallRaw = pick(raw, 'shortfall_vnd', 'shortfallVnd');
  const shortfallParsed =
    shortfallRaw == null || shortfallRaw === '' ? null : num(shortfallRaw);
  return {
    partnerBalanceVnd,
    merchantLiabilityVnd,
    shortfallVnd:
      shortfallParsed ??
      (partnerBalanceVnd != null
        ? Math.max(0, merchantLiabilityVnd - partnerBalanceVnd)
        : null),
    source: str(pick(raw, 'source', 'source')) || 'unknown',
    balanceSuccess,
    balanceError: str(pick(raw, 'balance_error', 'balanceError')) || null,
  };
}

function mapSubAccountDetail(raw: RawRecord): AppotaPaySubAccountDetail {
  const accountRaw = (raw.account as RawRecord | undefined) ?? raw;
  const payments = (raw.recentPayments ?? raw.recent_payments) as
    | RawRecord[]
    | undefined;
  return {
    ok: raw.ok as boolean | undefined,
    source: str(raw.source) ?? undefined,
    account: mapAppotaPaySubAccountRow(accountRaw),
    withdrawableVnd: num(pick(raw, 'withdrawable_vnd', 'withdrawableVnd')) ?? 0,
    withdrawableSource:
      str(pick(raw, 'withdrawable_source', 'withdrawableSource')) ||
      'sales_balance_vnd',
    recentPayments: (payments ?? []).map(p => ({
      id: num(p.id) ?? 0,
      referenceId: str(pick(p, 'reference_id', 'referenceId')) || '',
      transactionId: str(pick(p, 'transaction_id', 'transactionId')),
      status: str(p.status),
      amountVnd: num(pick(p, 'amount_vnd', 'amountVnd')),
      paidAmountVnd: num(pick(p, 'paid_amount_vnd', 'paidAmountVnd')),
      channel: str(p.channel),
      paidAt: str(pick(p, 'paid_at', 'paidAt')),
      createdAt: str(pick(p, 'created_at', 'createdAt')),
      updatedAt: str(pick(p, 'updated_at', 'updatedAt')),
    })),
    synced: raw.synced as boolean | undefined,
    balanceSuccess: raw.balanceSuccess as boolean | undefined,
    balanceError: str(raw.balanceError),
  };
}

export async function fetchAppotaPayPartnerBalance(refresh = false) {
  const res = await api.get('/admin/appotapay/balance', {
    params: refresh ? { refresh: 'true' } : undefined,
  });
  return mapPartnerBalance(unwrap<RawRecord>(res));
}

export async function fetchAppotaPaySubAccounts(params: {
  page?: number;
  limit?: number;
  status?: SubAccountStatus;
}) {
  const res = await api.get('/admin/appotapay/sub-accounts', { params });
  const body = res.data as {
    success?: boolean;
    data?: RawRecord[];
    pagination?: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
    message?: string;
  };
  const mapped = (body.data || [])
    .map(mapAppotaPaySubAccountRow)
    .filter(row => row.merchantId > 0);
  return {
    success: body.success !== false,
    data: mapped,
    pagination: body.pagination || { page: 1, limit: 20, total: 0, pages: 1 },
    message: body.message,
  };
}

export async function fetchAppotaPaySubAccountDetail(
  merchantId: number,
  refresh = false
) {
  const res = await api.get(`/admin/appotapay/sub-accounts/${merchantId}`, {
    params: refresh ? { refresh: true } : undefined,
  });
  return mapSubAccountDetail(unwrap<RawRecord>(res));
}

export async function syncAppotaPaySubAccount(merchantId: number) {
  const res = await api.post(
    `/admin/appotapay/sub-accounts/${merchantId}/sync`
  );
  return mapSubAccountDetail(
    unwrap<RawRecord>(res)
  ) as AppotaPaySubAccountDetail & { synced?: boolean };
}

export async function linkAppotaPaySubAccount(
  merchantId: number,
  body: {
    referId: string;
    partnerCode: string;
    apiKey: string;
    secretKey: string;
    environment?: string;
    note?: string;
  }
) {
  const res = await api.put(
    `/admin/appotapay/sub-accounts/${merchantId}`,
    body
  );
  return mapAppotaPaySubAccountRow(unwrap<RawRecord>(res));
}

export async function updateAppotaPaySubAccountStatus(
  merchantId: number,
  body: { status: SubAccountStatus; note?: string }
) {
  const res = await api.patch(
    `/admin/appotapay/sub-accounts/${merchantId}/status`,
    body
  );
  return mapAppotaPaySubAccountRow(unwrap<RawRecord>(res));
}

export async function lookupAppotaPayTransaction(
  merchantId: number,
  params: {
    referenceId: string;
    type?: 'PARTNER_ORDER_ID' | 'TRANSACTION_ID';
    force?: boolean;
  }
) {
  const res = await api.get(
    `/admin/appotapay/sub-accounts/${merchantId}/transaction-lookup`,
    { params }
  );
  return unwrap<LookupResult>(res);
}

export async function lookupAppotaPayRefund(
  merchantId: number,
  params: {
    partnerRefId?: string;
    transactionId?: string;
    type?: 'PARTNER_ORDER_ID' | 'TRANSACTION_ID';
    force?: boolean;
  }
) {
  const res = await api.get(
    `/admin/appotapay/sub-accounts/${merchantId}/refund-lookup`,
    { params }
  );
  return unwrap<LookupResult>(res);
}

export async function fetchAppotaPayLookupHistory(
  merchantId: number,
  params?: { lookupType?: 'transaction' | 'refund'; limit?: number }
) {
  const res = await api.get(
    `/admin/appotapay/sub-accounts/${merchantId}/lookup-history`,
    { params }
  );
  return unwrap<
    Array<{
      id: number;
      lookupType: string;
      lookupKey: string;
      syncedAt: string;
      payload: unknown;
    }>
  >(res);
}
